import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../lib/api";
import { money } from "../../lib/pricing";
import { Button } from "../ui/button";
import { Spinner } from "../hs/hsUtils";
import { Bank, ArrowsDownUp, TrendUp, TrendDown, Receipt, HandCoins, Warning } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

function Kpi({ label, value, hint, tone = "", icon: Icon, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1">{Icon && <Icon size={12} />} {label}</p>
      <p className={`text-2xl font-bold font-[Chivo] mt-1 ${tone}`}>{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

/** Accounts overview: cash position, unexplained lines, this month's P&L, VAT estimate, debtors / creditors and a 12-month chart. */
export function AccountsOverview({ onGo }) {
  const [d, setD] = useState(null);
  useEffect(() => { API.get("/accounts/overview").then((r) => setD(r.data)).catch(() => setD({})); }, []);
  if (!d) return <Spinner />;
  if (!d.has_data) {
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center space-y-3" data-testid="accounts-empty">
        <Bank size={36} className="mx-auto text-muted-foreground" />
        <h2 className="text-base md:text-lg font-semibold">Start with a bank account</h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">Add your business current account, then import a statement (CSV, OFX or QIF) or connect a live bank feed. Explain each line and your P&amp;L, balance sheet and VAT position build themselves.</p>
        <div className="flex justify-center gap-2">
          <Button onClick={() => onGo("banking")} data-testid="accounts-empty-add-bank">Add a bank account</Button>
          <Button variant="outline" onClick={() => onGo("settings")} data-testid="accounts-empty-settings">Set up VAT &amp; company details</Button>
        </div>
        {(d.creditors_total > 0 || d.bills_to_check > 0) && (
          <button type="button" onClick={() => onGo("bills")} className="text-xs text-muted-foreground underline hover:text-foreground" data-testid="accounts-empty-bills-link">
            Meanwhile: you owe suppliers {money(d.creditors_total)}{d.creditors_overdue ? ` (${money(d.creditors_overdue)} overdue)` : ""}{d.bills_to_check ? ` · ${d.bills_to_check} snapped bill${d.bills_to_check === 1 ? "" : "s"} to check` : ""} → Bills
          </button>
        )}
      </div>
    );
  }
  const m = d.month || {};
  return (
    <div className="space-y-5" data-testid="accounts-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Cash at bank" value={money(d.cash)} hint={`${d.bank_accounts.length} account${d.bank_accounts.length === 1 ? "" : "s"}`} icon={Bank} testid="ov-cash" />
        <Kpi label="Unexplained" value={d.unexplained} hint={d.unexplained ? "bank lines to explain" : "all caught up"} tone={d.unexplained ? "text-amber-600" : "text-emerald-600"} icon={ArrowsDownUp} testid="ov-unexplained" />
        <Kpi label="This month in" value={money(m.income || 0)} hint="income received (cash basis)" icon={TrendUp} testid="ov-month-in" />
        <Kpi label="This month out" value={money((m.cost_of_sales || 0) + (m.overheads || 0))} hint={`profit ${money(m.net_profit || 0)}`} icon={TrendDown} testid="ov-month-out" />
        <Kpi label="Owed to you" value={money(d.debtors_total)} hint={d.debtors_overdue ? `${money(d.debtors_overdue)} overdue` : "nothing overdue"} tone={d.debtors_overdue ? "text-amber-600" : ""} icon={Receipt} testid="ov-debtors" />
        <button type="button" onClick={() => onGo("bills")} className="text-left rounded-lg hover:ring-2 hover:ring-primary/40 transition-shadow" data-testid="ov-creditors-link"><Kpi label="You owe" value={money(d.creditors_total)} hint={d.creditors_overdue ? `${money(d.creditors_overdue)} overdue${d.bills_to_check ? ` · ${d.bills_to_check} to check` : ""}` : d.bills_to_check ? `${d.bills_to_check} bill${d.bills_to_check === 1 ? "" : "s"} to check` : "open bills & in-house POs"} tone={d.creditors_overdue ? "text-amber-600" : ""} icon={HandCoins} testid="ov-creditors" /></button>
        {d.vat ? <Kpi label="VAT due (estimate)" value={money(d.vat.vat_due)} hint={`${d.vat_period.label} · file by ${new Date(d.vat_period.due).toLocaleDateString("en-GB")}`} tone={d.vat.vat_due > 0 ? "text-rose-600" : "text-emerald-600"} icon={Warning} testid="ov-vat" />
          : <Kpi label="VAT" value="Not registered" hint="switch on in Settings if you are" testid="ov-vat" />}
      </div>
      <div className="rounded-lg border border-border bg-card p-4" data-testid="ov-chart">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold">Last 12 months · cash basis</h2>
          <Link to="/accounts/reports" className="text-xs text-primary hover:underline" data-testid="ov-reports-link">Open reports</Link>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d.series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => money(v, { compact: true })} width={56} />
              <Tooltip formatter={(v, n) => [money(v), n]} />
              <Bar dataKey="income" name="Income" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
              <Bar dataKey="costs" name="Costs" radius={[3, 3, 0, 0]}>{d.series.map((_, i) => <Cell key={i} fill="#F59E0B" />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {d.unexplained > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 flex flex-wrap items-center gap-3 text-sm" data-testid="ov-explain-nudge">
          <Warning size={16} className="text-amber-600" /> <span>{d.unexplained} bank line{d.unexplained === 1 ? "" : "s"} still need explaining before your reports are complete.</span>
          <Button size="sm" className="ml-auto" onClick={() => onGo("transactions")} data-testid="ov-explain-btn">Explain now</Button>
        </div>
      )}
    </div>
  );
}
